import { useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Zap, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LOGO_IMAGE } from "@/lib/site";

export default function Login() {
    const { user, loading, startLogin } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const err = params.get("error");

    useEffect(() => {
        document.title = "Admin Login | 90 Day Alpha";
    }, []);

    useEffect(() => {
        if (!loading && user?.is_admin) {
            navigate("/admin", { replace: true });
        }
    }, [user, loading, navigate]);

    return (
        <div
            data-testid="login-page"
            className="min-h-[100vh] electric-bg flex items-center justify-center px-6 py-20 relative overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#1E90FF]/15 blur-[160px] pointer-events-none"
            />
            <div className="relative max-w-md w-full">
                <div className="text-center mb-10">
                    <img
                        src={LOGO_IMAGE}
                        alt="90 Day Alpha"
                        className="h-20 mx-auto drop-shadow-[0_0_28px_rgba(30,144,255,0.5)]"
                    />
                </div>
                <div className="bg-[#060c1e] border border-[#1a2a4a] p-8 md:p-10">
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Zap
                            size={14}
                            className="text-[#00B7FF]"
                            fill="currentColor"
                        />
                        <p className="text-xs uppercase tracking-[0.28em] font-semibold text-[#00B7FF]">
                            Admin Console
                        </p>
                    </div>
                    <h1 className="font-display font-black text-white text-3xl tracking-tight">
                        Sign in to{" "}
                        <span className="text-[#D4AF37]">command</span>.
                    </h1>
                    <p className="mt-3 text-white/70 text-sm leading-relaxed">
                        Restricted to authorized 90 Day Alpha team accounts.
                        Sign in with the admin Google account to access bookings,
                        clients, and finances.
                    </p>

                    {err && (
                        <div className="mt-5 border border-[#8B0000]/50 bg-[#1a0606] text-[#fca5a5] text-xs px-4 py-3">
                            Authentication failed. Please try again or contact
                            support.
                        </div>
                    )}

                    <button
                        type="button"
                        data-testid="google-login-button"
                        onClick={startLogin}
                        className="mt-8 inline-flex items-center justify-center gap-2 w-full bg-[#D4AF37] text-black font-bold uppercase tracking-[0.18em] text-xs px-8 py-4 hover:bg-[#C5A059] transition-all shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                    >
                        Continue With Google <ArrowUpRight size={16} />
                    </button>
                    <p className="mt-5 text-[11px] text-white/45 text-center">
                        Powered by Emergent · Single-admin allow-list
                    </p>
                </div>

                <div className="mt-8 text-center">
                    <Link
                        to="/"
                        data-testid="login-back-link"
                        className="text-white/55 hover:text-[#00B7FF] uppercase tracking-[0.2em] text-[11px] font-semibold"
                    >
                        ← Back to website
                    </Link>
                </div>
            </div>
        </div>
    );
}
